import { Grid2 } from '@mui/material';
import dayjs from 'dayjs';
import type { FieldProps } from 'formik';
import { Field, useFormikContext } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { EffectiveDateTimeInput } from '@components/DateInput';

import { getFhirDateComponents, isYear, isYearMonth } from '@utils/fhir';

const messages = defineMessages({
  unknownDateYear: {
    id: 'formikFhirDateField.unknownDateYear',
    defaultMessage: 'If date is unknown, enter the year',
  },
  dateTime: {
    id: 'formikFhirDateField.dateTime',
    defaultMessage: '{prefix} Date and Time',
  },
  date: {
    id: 'formikFhirDateField.date',
    defaultMessage: '{prefix} Date',
  },
});

interface FormikFhirDateFieldProps {
  name: string;
  label?: string;
  error?: string;
  /**
   * If `true`, sets the max date to today's date.
   * If a date string is provided, it is used as the max date directly
   * (useful when a field allows future dates but still needs a bounded
   * max so native date inputs cap year entry at 4 digits).
   */
  max?: boolean | string;
  /**
   * Earliest selectable date, as a FHIR date string. A native input affordance
   * only — validation is what actually blocks submission.
   */
  min?: string | null;
  required?: boolean;
  /**
   * Callback that fires when the value of [one of] the input(s) change
   *
   * `value` may be in the format YYYY, YYYY-MM-DD, or YYYY-MM-DDTHH:mm:ssZ
   */
  onChange?: (value: string | null) => void;
  formats: DateFormat[];
  value?: string | null;
  prefix?: string;
}

const FormikFhirDateField: React.FC<FormikFhirDateFieldProps> = ({
  ...props
}) => {
  const {
    name,
    label,
    required,
    onChange,
    formats,
    value = null,
    prefix,
    max = false,
    min,
  } = props;

  const { formatMessage } = useIntl();
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const [dateValues, setDateValues] = React.useState<{
    datetime: string | null;
    date: string | null;
    month: string | null;
    year: string | null;
  }>({
    datetime: null,
    date: null,
    month: null,
    year: null,
  });

  React.useEffect(() => {
    const components = getFhirDateComponents(value);
    if (!components) {
      setDateValues({
        datetime: null,
        date: null,
        month: null,
        year: null,
      });
      return;
    }

    const { day, month, year, time } = components;
    const format = time
      ? 'datetime'
      : day
        ? 'date'
        : month
          ? 'month'
          : year
            ? 'year'
            : undefined;

    if (format && value) {
      const formattedDatetime = dayjs(value).format('YYYY-MM-DDTHH:mm');
      const formattedDate = dayjs(value).format('YYYY-MM-DD');
      const formattedMonth = dayjs(value).format('YYYY-MM');
      const formattedYear = dayjs(value).format('YYYY');
      setDateValues({
        datetime: format === 'datetime' ? formattedDatetime : null,
        date: format === 'date' ? formattedDate : null,
        month: format === 'month' ? formattedMonth : null,
        year: format === 'year' ? formattedYear : null,
      });
    }
  }, [value]);

  const handleChange = React.useCallback(
    (format: DateFormat, formattedValue: string | null) => {
      setDateValues({
        datetime: format === 'datetime' ? formattedValue : null,
        date: format === 'date' ? formattedValue : null,
        month: format === 'month' ? formattedValue : null,
        year: format === 'year' ? formattedValue : null,
      });
      // Surface validation errors as soon as the value changes. These inputs can
      // sit in a drilldown header while the submit button sits at the foot of
      // the form, so waiting for blur means the user never sees why submitting
      // did nothing. `setFieldValue` below already triggers a validation pass,
      // so this one does not need to.
      setFieldTouched(name, true, false);
      return onChange
        ? onChange(formattedValue)
        : setFieldValue(name, formattedValue);
    },
    [onChange, setFieldValue, setFieldTouched, name]
  );

  /**
   * Every rendered format is bound to the same Formik field, so without this
   * each input would repeat the identical helper text. Only one format holds a
   * value at a time, so anchor the message to that one.
   */
  const errorFormat = React.useMemo(() => {
    const active = (['datetime', 'date', 'month', 'year'] as const).find(
      (format) => formats.includes(format) && dateValues[format]
    );
    return active ?? formats[0];
  }, [formats, dateValues]);

  const renderInput = React.useCallback(
    (
      format: DateFormat,
      inputLabel: string,
      value: string | null,
      onChange: (value: string | null) => void
    ) => {
      const handleChange = (v: string | null) => {
        onChange(v);
      };

      const shouldSkipStringMaxForYear =
        format === 'year' && typeof max === 'string';

      const inputMax =
        max && !shouldSkipStringMaxForYear
          ? dayjs(typeof max === 'string' ? max : undefined).format(
              format === 'datetime' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'
            )
          : undefined;

      // `min` can be a partial FHIR date (year or year-month, as Auto-Curation
      // hands us) — pad it out to a full calendar date first so every format
      // below slices out a value its native input actually accepts. Padding
      // with literal "-01"s is safe (unlike the full-precision case below)
      // because there's no time-of-day component for a UTC offset to disturb.
      const paddedMin = isYear(min ?? '')
        ? `${min}-01-01`
        : isYearMonth(min ?? '')
          ? `${min}-01`
          : min;

      // Slice rather than reformat the full-precision case: the datetime input
      // renders its value in UTC, so a locally formatted bound would be off by
      // the UTC offset. Year inputs are a <select> whose options run min..max,
      // and a future min would leave it empty, so they opt out — validation
      // still enforces the rule.
      const inputMin =
        paddedMin && format !== 'year'
          ? format === 'datetime'
            ? paddedMin.length >= 16
              ? paddedMin.slice(0, 16)
              : `${paddedMin.slice(0, 10)}T00:00`
            : format === 'month'
              ? paddedMin.slice(0, 7)
              : paddedMin.slice(0, 10)
          : undefined;

      return (
        <Grid2 key={`${format}-input`} size={{ xs: 'auto' }}>
          <Field name={name}>
            {({ field, meta }: FieldProps<string>) => (
              <EffectiveDateTimeInput
                error={meta.touched && format === errorFormat ? meta.error : ''}
                label={inputLabel}
                required={required}
                {...field}
                onChange={handleChange}
                value={value}
                name={name}
                max={inputMax}
                min={inputMin}
                type={format}
              />
            )}
          </Field>
        </Grid2>
      );
    },
    [max, min, name, required, errorFormat]
  );

  const inputs = React.useMemo(() => {
    return formats.map((format) => {
      switch (format) {
        case 'datetime':
          return renderInput(
            format,
            label ? label : formatMessage(messages.dateTime, { prefix }),
            dateValues.datetime,
            (value) => handleChange(format, value)
          );
        case 'date':
          return renderInput(
            format,
            label ? label : formatMessage(messages.date, { prefix }),
            dateValues.date,
            (value) => handleChange(format, value)
          );
        case 'month':
          return renderInput(format, label ?? '', dateValues.month, (value) =>
            handleChange(format, value)
          );
        case 'year':
          return renderInput(
            format,
            formatMessage(messages.unknownDateYear),
            dateValues.year,
            (value) => handleChange(format, value)
          );
        default:
          return null;
      }
    });
  }, [
    formats,
    renderInput,
    label,
    formatMessage,
    prefix,
    dateValues.datetime,
    dateValues.date,
    dateValues.month,
    dateValues.year,
    handleChange,
  ]);

  return (
    <Grid2 container direction="row" spacing={1} sx={{ width: '100%' }}>
      {inputs}
    </Grid2>
  );
};

export default FormikFhirDateField;
